import { z } from 'zod'

import {
  AdmissionGroupStatus,
  AdmissionGroupPlatformType,
  AdmissionGroupType,
  AdmissionGroupCreatedBy,
  AdmissionGroupCancelledReason,
  AdmissionGroupCancelledBy,
  AdmissionGroupCompType,
  AdmissionGroupReceiptType,
  AdmissionGroupFraudRisk,
} from '../constants/admissionGroup'
import { BookingPaymentType } from '../constants/booking'
import { bookingFeeSchema } from './booking'
import { PaymentStatus } from '../constants/pricing'
import { InventoryType } from '../constants/inventory'
import { customerFlagsSchema, customerTagsSchema } from './customer'
import { AttributionSourceType } from '../constants/attributionSource'
import { VenuePosType } from '../constants/venue'
import {
  dbEntitySchema,
  listQueryOptionsSchema,
  listQueryZodFields,
  submitterContextSchema,
} from './system'
import { admissionGroupCustomerTagEmbeddedSchema } from './customerTag'
import { admissionGroupTagBaseSchema } from './admissionGroupTag'
import { paymentInstrumentSnapshotSchema } from './paymentInstrument'

export const admissionGroupGuestCountSchema = z.object({
  inventory: z.string().nullish(),
  inventoryPrice: z.number().nullish(),
  inventoryType: z.nativeEnum(InventoryType).nullish(),
  inventoryMembershipLevel: z.number().nullish(),
  inventoryName: z.string().nullish(),
  inventoryDescription: z.string().nullish(),
  guestsExpected: z.number().nullish(),
  guestsExpectedUnisex: z.number().nullish(),
  guestsExpectedMale: z.number().nullish(),
  guestsExpectedFemale: z.number().nullish(),
  guestsCheckedIn: z.number().nullish(),
  guestsCheckedInUnisex: z.number().nullish(),
  guestsCheckedInMale: z.number().nullish(),
  guestsCheckedInFemale: z.number().nullish(),
})

export const admissionGroupPosPaymentSchema = z.object({
  id: z.string().nullish(),
  type: z.string().nullish(),
  tenderType: z.string().nullish(),
  fullName: z.string().nullish(),
  last4: z.string().nullish(),
  amount: z.number().nullish(),
  tip: z.number().nullish(),
})

export const admissionGroupPosTicketSchema = z.object({
  _id: z.string(),
  name: z.string().nullish(),
  serverId: z.string().nullish(),
  serverName: z.string().nullish(),
  open: z.boolean().nullish(),
  openedAt: z.coerce.date().nullish(),
  closedAt: z.coerce.date().nullish(),
  guestCount: z.number().nullish(),
  ticketId: z.string().nullish(),
  ticketNumber: z.number().nullish(),
  tableName: z.string().nullish(),
  tableId: z.string().nullish(),
  subtotal: z.number().nullish(),
  serviceCharges: z.number().nullish(),
  tax: z.number().nullish(),
  tip: z.number().nullish(),
  total: z.number().nullish(),
  due: z.number().nullish(),
  paid: z.number().nullish(),
  discounts: z.number().nullish(),
  payments: z.array(admissionGroupPosPaymentSchema).optional().nullable(),
  items: z
    .array(
      z.object({
        id: z.string().nullish(),
        name: z.string().nullish(),
        tenderType: z.string().nullish(),
        comment: z.string().nullish(),
        price: z.number().nullish(),
        quantity: z.number().nullish(),
        split: z.number().nullish(),
        discount: z.number().nullish(),
      })
    )
    .optional()
    .nullable(),
})

export const admissionGroupPosTicketCreateArgsSchema =
  admissionGroupPosTicketSchema.pick({
    ticketNumber: true,
    items: true,
    payments: true,
    subtotal: true,
    open: true,
    serverId: true,
    serverName: true,
  })

export const admissionGroupPosOrderSchema = z.object({
  id: z.string(),
  posType: z.nativeEnum(VenuePosType),
  ticketId: z.string().nullish(),
  ticketNumber: z.number().nullish(),
  tableName: z.string().nullish(),
  customerName: z.string().nullish(),
  open: z.boolean().nullish(),
  openedAt: z.coerce.date().nullish(),
  closedAt: z.coerce.date().nullish(),
  subtotal: z.number().nullish(),
  tax: z.number().nullish(),
  tip: z.number().nullish(),
  total: z.number().nullish(),
})

export const admissionGroupPosOrderListQuerySchema = z.object({
  date: z.coerce.date().optional(),
})

export const admissionGroupPosOrderListResponseSchema = z.object({
  data: z.array(admissionGroupPosOrderSchema),
})

/** Stored receipt fee rows may omit fields present on live booking pricing. */
const admissionGroupReceiptFeeSchema = bookingFeeSchema.partial()

export const admissionGroupReceiptSchema = z.object({
  id: z.string().optional(),
  confirmationRequest: z.string().nullish(),
  booking: z.string().nullish(),
  type: z.nativeEnum(AdmissionGroupReceiptType).nullish(),
  name: z.string().nullish(),
  customer: z.string().nullish(),
  // User info
  userName: z.string().nullish(),
  userEmail: z.string().nullish(),
  userPhone: z.string().nullish(),
  // Payment details
  ...paymentInstrumentSnapshotSchema.shape,
  // Payment breakdown
  paymentType: z.nativeEnum(BookingPaymentType).nullish(),
  price: z.number().min(0).nullish(),
  subtotal: z.number().nullish(),
  minimum: z.number().min(0).nullish(),
  additionalSpend: z.number().min(0).default(0).nullish(),
  taxAndTip: z.number().min(0).default(0).nullish(),
  tax: z.number().min(0).default(0).nullish(),
  taxPercent: z.number().min(0).default(0).nullish(),
  tip: z.number().min(0).default(0).nullish(),
  tipPercent: z.number().min(0).default(0).nullish(),
  fees: z.array(admissionGroupReceiptFeeSchema).optional().nullable(),
  processingFees: z.array(admissionGroupReceiptFeeSchema).optional().nullable(),
  feeTotal: z.number().min(0).default(0).nullish(),
  venueFeeTotal: z.number().min(0).default(0).nullish(),
  tablelistFeeTotal: z.number().min(0).default(0).nullish(),
  processingFeeTotal: z.number().min(0).default(0).nullish(),
  total: z.number().min(0).default(0).nullish(),
  dueOnArrival: z.number().min(0).default(0).nullish(),
  tablelistCommision: z.number().min(0).default(0).nullish(),
  venuePayout: z.number().min(0).default(0).nullish(),
})

export const admissionGroupOrderBreakdownSchema = z.object({
  price: z.number().default(0).nullish(),
  subtotal: z.number().default(0).nullish(),
  additionalSpend: z.number().default(0).nullish(),
  tax: z.number().default(0).nullish(),
  tip: z.number().default(0).nullish(),
  venueFeeTotal: z.number().default(0).nullish(),
  processingFeeTotal: z.number().default(0).nullish(),
  userPaidProcessingFeeTotal: z.number().default(0).nullish(),
  venueAbsorbedProcessingFeeTotal: z.number().default(0).nullish(),
  paymentProviderProcessingFee: z.number().default(0).nullish(),
  paymentProviderProcessingFeeUsd: z.number().default(0).nullish(),
  paymentProviderApplicationFee: z.number().default(0).nullish(),
  paymentProviderApplicationFeeUsd: z.number().default(0).nullish(),
  paymentProviderChargeUsd: z.number().default(0).nullish(),
  total: z.number().default(0).nullish(),
  venuePayout: z.number().default(0).nullish(),
})

export const admissionGroupOrderSchema = z.object({
  booking: z.string(),
  sale: z.string().nullish(),
  paymentProviderAccountId: z.string().nullish(),
  paymentProviderSettlementCurrencyCode: z.string().nullish(),
  paymentProviderExchangeRate: z.number().nullish(),
  payment: z.nativeEnum(BookingPaymentType).nullish(),
  date: z.coerce.date().nullish(),
  purchaseDate: z.coerce.date().nullish(),
  inventoryType: z.nativeEnum(InventoryType).nullish(),
  breakdown: admissionGroupOrderBreakdownSchema.nullish(),
})

export const admissionGroupFlagsSchema = z.object({
  birthday: z.boolean().nullish(),
  sign_request: z.boolean().nullish(),
  bachelorette: z.boolean().nullish(),
  bachelor: z.boolean().nullish(),
  corporate_event: z.boolean().nullish(),
  per_owner: z.boolean().nullish(),
  per_manager: z.boolean().nullish(),
  nfu: z.boolean().nullish(),
  promoter: z.boolean().nullish(),
  paid_cash: z.boolean().nullish(),
  paid_venmo: z.boolean().nullish(),
  paid_cashapp: z.boolean().nullish(),
  girls_night_out: z.boolean().nullish(),
  paid_zelle: z.boolean().nullish(),
  paid_via_wire: z.boolean().nullish(),
  paid_via_bank_transfer: z.boolean().nullish(),
  paid_via_crypto: z.boolean().nullish(),
})

export const admissionGroupTableSchema = z.object({
  id: z.string(),
  name: z.string(),
})

export const admissionGroupCustomerSchema = z.object({
  customer: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  gender: z.string().nullish(),
  birthdayDay: z.number().nullish(),
  birthdayMonth: z.number().nullish(),
  birthdayYear: z.number().nullish(),
  birthday: z.coerce.date().nullish(),
  profileImage: z.string().nullish(),
  notes: z.string().nullish(),
})

export const admissionGroupNotificationChannelSchema = z.object({
  lastSentAt: z.coerce.date().nullish(),
  count: z.number().nullish(),
})

export const admissionGroupSchema = dbEntitySchema.extend({
  importId: z.string().nullish(),
  date: z.date(),
  user: z.string().nullish(),
  inventory: z.string().nullish(),
  inventoryName: z.string().nullish(),
  inventoryPrice: z.number().nullish(),
  inventoryType: z.nativeEnum(InventoryType).nullish(),
  inventoryDescription: z.string().nullish(),
  inventoryCheckInStart: z.date().nullish(),
  inventoryCheckInEnd: z.date().nullish(),
  inventoryMembershipLevel: z.number().nullish(),
  confirmationRequested: z.boolean().nullish(),
  customerNotifications: z
    .object({
      confirmationEmail: admissionGroupNotificationChannelSchema.nullish(),
      confirmationSms: admissionGroupNotificationChannelSchema.nullish(),
    })
    .nullish(),
  hasPendingPayment: z.boolean().nullish(),
  prepaid: z.boolean().nullish(),
  paymentStatus: z.nativeEnum(PaymentStatus).nullish(),
  pricePerGuest: z.number().nullish(),
  minimumSpend: z.number().nullish(),
  minimumBottles: z.number().nullish(),
  /**
   * Net revenue value (same calculation as `spent`).
   * Kept in parallel with `spent` for terminology migration.
   */
  netRevenue: z.number().nullish(),
  /**
   * @deprecated - don't use anymore, use `netRevenue` instead
   */
  spent: z.number().nullish(),
  saleDate: z.date().nullish(),
  saleExportDate: z.date().nullish(),
  salePurchaseDate: z.date().nullish(),
  prepaidSubtotal: z.number().nullish(),
  prepaidTax: z.number().nullish(),
  prepaidTip: z.number().nullish(),
  prepaidVenueFees: z.number().nullish(),
  prepaidProcessingFees: z.number().nullish(),
  prepaidTotal: z.number().nullish(),
  prepaidVenuePayout: z.number().nullish(),
  finalSpent: z.number().nullish(),
  compedTickets: z.number().nullish(),
  compedBottles: z.number().nullish(),
  /**
   * @deprecated - don't use anymore
   */
  posSyncEnabled: z
    .boolean()
    .nullish()
    .describe(
      'Client UI only (e.g. show POS code on printed chits). Does not gate server spent, POS integrations, or sale sync.'
    ),
  posCode: z.string().nullish(),
  leadInProgressAt: z.coerce.date().nullish(),
  currencyCode: z.string().nullish(),
  createdDiary: z.date(),
  venue: z.string(),
  organization: z.string(),
  server: z.string().nullish(),
  serverName: z.string().nullish(),
  event: z.string().nullish(),
  eventName: z.string().nullish(),
  eventDateStart: z.coerce.date().nullish(),
  eventDateEnd: z.coerce.date().nullish(),
  type: z.nativeEnum(AdmissionGroupType),
  status: z.nativeEnum(AdmissionGroupStatus),
  createdById: z.string().nullish(),
  createdByName: z.string().nullish(),
  createdByType: z.nativeEnum(AdmissionGroupCreatedBy).nullish(),
  canceled: z.boolean().nullish(),
  cancelledReasonType: z.nativeEnum(AdmissionGroupCancelledReason).nullish(),
  cancelledByType: z.nativeEnum(AdmissionGroupCancelledBy).nullish(),
  cancelledById: z.string().nullish(),
  cancelledByName: z.string().nullish(),
  cancelledNote: z.string().nullish(),
  compType: z.nativeEnum(AdmissionGroupCompType).nullish(),
  name: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullish(),
  administrativeArea: z.string().nullish(),
  phone: z.string().nullish(),
  /**
   * @deprecated Prefer `tags` (AdmissionGroupTag). Still written for backward compatibility; merged into `tags`.
   */
  flags: admissionGroupFlagsSchema.nullish(),
  /** Venue-defined custom substatus label, scoped to this admission group's `type`. See getConfiguredSubstatuses. */
  substatus: z.string().nullish(),
  tags: z.array(admissionGroupTagBaseSchema).nullish(),
  customerTags: customerTagsSchema.nullish(),
  /**
   * @deprecated use customerTags instead
   */
  customerFlags: customerFlagsSchema.nullish(),
  customer: z.string().nullish(),
  customers: z.array(admissionGroupCustomerSchema).nullish(),
  assignedTo: z.string().nullish(),
  platform: z.nativeEnum(AdmissionGroupPlatformType).nullish(),
  orders: z.array(admissionGroupOrderSchema).nullish(),
  bookings: z.array(z.string()).nullish(),
  bookingUsers: z
    .array(
      z.object({
        booking: z.string(),
        user: z.string().nullish(),
        prospect: z.string().nullish(),
        phone: z.string().nullish(),
        email: z.string().nullish(),
        name: z.string().nullish(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
      })
    )
    .optional()
    .nullable(),
  tables: z.array(admissionGroupTableSchema).optional().nullable(),
  sales: z.array(z.string()).nullish(),
  reviews: z.array(z.string()).nullish(),
  attributionId: z.string().nullish(),
  attributionName: z.string().nullish(),
  attributionType: z.string().nullish(),
  guestCounts: z.array(admissionGroupGuestCountSchema).nullish(),
  ticketToken: z.string().nullish(),
  guestsExpected: z.number().nullish(),
  guestsExpectedUnisex: z.number().nullish(),
  guestsExpectedMale: z.number().nullish(),
  guestsExpectedFemale: z.number().nullish(),
  guestsCheckedIn: z.number().nullish(),
  guestsCheckedInUnisex: z.number().nullish(),
  guestsCheckedInMale: z.number().nullish(),
  guestsCheckedInFemale: z.number().nullish(),
  guestsCheckedInTotal: z.number().nullish(),
  // payment risk + fraud
  fraudRiskScore: z.number().nullish(),
  fraudRisk: z.nativeEnum(AdmissionGroupFraudRisk).nullish(),
  fraudRiskUpdated: z.date().nullish(),
  //integration data
  aiq: z
    .object({
      contactId: z.string().nullish(),
      srcId: z.string().nullish(),
    })
    .nullish(),
  insightly: z
    .object({
      id: z.string().nullish(),
    })
    .nullish(),
  eventbrite: z
    .object({
      id: z.string().nullish(),
    })
    .nullish(),
  silverware: z
    .object({
      id: z.string().nullish(),
    })
    .nullish(),
  platformType: z.nativeEnum(AdmissionGroupPlatformType).nullish(),
  posSynced: z.boolean().nullish(),
  closed: z.boolean().nullish(),
  booking: z.string().nullish(),
  //
  receipts: z.array(admissionGroupReceiptSchema).nullish(),
  posTickets: z.array(admissionGroupPosTicketSchema).nullish(),
  //
  deviceName: z.string().nullish(),
  arrived: z.boolean().nullish(),
  arrivedAt: z.date().nullish(),
  arrivalTime: z.date().nullish(),
  arrivalTimeHour: z.number().nullish(),
  arrivalTimeMinute: z.number().nullish(),
  checkedInTime: z.date().nullish(),
  checkedInTimeHour: z.number().nullish(),
  checkedInTimeMinute: z.number().nullish(),
  endTime: z.date().nullish(),

  gender: z.string().nullish(),
  birthdayDay: z.number().nullish(),
  birthdayMonth: z.number().nullish(),
  birthdayYear: z.number().nullish(),
  vip: z.boolean().nullish(),
  customerLastIncomingMessageDate: z.date().nullish(),
  customerLastOutgoingMessageDate: z.date().nullish(),
  customerMarketingOptIn: z.boolean().nullish(),
  customerMarketingOptInSignature: z.string().nullish(),
  submitter: submitterContextSchema.nullish(),
  orderSummary: z.string().nullish(),
  notes: z.string().nullish(),
  comp: z.boolean().nullish(),
  modifiedBy: z.string().nullish(),
  lead: z.boolean().nullish(),

  paymentDetail: z
    .object({
      amount: z.number().min(0).nullish(),
      ...paymentInstrumentSnapshotSchema.shape,
    })
    .nullish(),
})

export const admissionGroupCreateArgsSchema = admissionGroupSchema
  .omit({
    id: true,
    _id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venue: true,
    server: true,
    event: true,
    serverName: true,
    inventory: true,
    status: true,
    customer: true,
    user: true,
    inventoryDescription: true,
    inventoryCheckInStart: true,
    inventoryCheckInEnd: true,
    inventoryMembershipLevel: true,
    confirmationRequested: true,
    customerNotifications: true,
    hasPendingPayment: true,
    prepaid: true,
    inventoryPrice: true,
    inventoryType: true,
    inventoryName: true,
    cancelledByName: true,
    cancelledNote: true,
    createdDiary: true,
    lead: true,
    currencyCode: true,
    canceled: true,
    // cancelledReasonType: true,
    // cancelledByType: true,
    // cancelledById: true,
    // platform: true,
    bookings: true,
    bookingUsers: true,
    customers: true,
    sales: true,
    reviews: true,
    platform: true,
    arrivedAt: true,
    checkedInTime: true,
    checkedInTimeHour: true,
    checkedInTimeMinute: true,
    arrivalTimeHour: true,
    arrivalTimeMinute: true,
    customerTags: true,
    customerFlags: true,
    tags: true,
    submitter: true,
    createdByType: true,
  })
  .extend({
    managementUserId: z.string().nullish(),
    userId: z.string().nullish(),
    inventoryLevel: z.string().nullish(),
    customerId: z.string().nullish(),
    createdDiary: z.date().nullish(),
    // customer: z.string().nullish(),
    venueId: z.string(),
    serverId: z.string().nullish(),
    eventId: z.string().nullish(),
    inventoryId: z.string().nullish(),
    inventory: z.string().nullish(),
    name: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    administrativeArea: z.string().nullish(),
    phone: z.string().nullish(),
    status: z.nativeEnum(AdmissionGroupStatus).nullish(),
    type: z.nativeEnum(AdmissionGroupType).nullish(),
    platform: z.nativeEnum(AdmissionGroupPlatformType).optional(),
    sales: z.array(z.string()).optional(),
    admissionGroupTagIds: z.array(z.string()).nullish(),
    tags: z.array(admissionGroupTagBaseSchema).nullish(),
    customerTags: z.array(admissionGroupCustomerTagEmbeddedSchema).nullish(),
  })

export const admissionGroupUpdateByIdArgsSchema = admissionGroupSchema
  .partial()
  .omit({
    id: true,
    _id: true,
    organization: true,
    venue: true,
    customer: true,
    server: true,
    event: true,
    inventory: true,
    created: true,
    createdDiary: true,
    modified: true,
    hasPendingPayment: true,
    customerNotifications: true,
    type: true,
    canceled: true,
    // cancelledReasonType: true,
    // cancelledByType: true,
    // cancelledById: true,
    bookings: true,
    bookingUsers: true,
    customers: true,
    reviews: true,
    sales: true,
    customerTags: true,
    tags: true,
  })
  .extend({
    date: z.date().optional(),
    type: z.nativeEnum(AdmissionGroupType).optional(),
    // organizationId: z.string().optional(),
    // venueId: z.string(),
    customerId: z.string().optional(),
    serverId: z.string().optional(),
    eventId: z.string().nullish(),
    inventoryId: z.string().optional(),
    managementUserId: z.string().optional(),
    canceled: z.boolean().nullish(),
    posAutoClose: z.boolean().optional(),
    customerTags: z.array(admissionGroupCustomerTagEmbeddedSchema).nullish(),
    tags: z.array(admissionGroupTagBaseSchema).nullish(),
    admissionGroupTagIds: z.array(z.string()).nullish(),
  })

export const admissionGroupListQuerySchema = listQueryOptionsSchema.extend({
  organizationId: z.string().optional(),
  venueId: z.string().or(z.array(z.string())).optional(),
  /** Alias of venueId used by v0 list filters */
  venue: z.string().or(z.array(z.string())).optional(),
  customerId: z.string().optional(),
  eventId: z.string().or(z.array(z.string())).optional(),
  /** Alias of eventId used by v0 list filters */
  event: z.string().or(z.array(z.string())).optional(),
  dateStart: z.coerce.date().optional(),
  dateEnd: z.coerce.date().optional(),
  modifiedStart: z.coerce.date().optional(),
  createdStart: z.coerce.date().optional(),
  createdEnd: z.coerce.date().optional(),
  type: z
    .nativeEnum(AdmissionGroupType)
    .or(z.array(z.nativeEnum(AdmissionGroupType)))
    .optional(),
  status: z
    .nativeEnum(AdmissionGroupStatus)
    .or(z.array(z.nativeEnum(AdmissionGroupStatus)))
    .optional(),
  substatus: z.string().or(z.array(z.string())).optional(),
  attributionId: z.string().optional(),
  attributionType: z.nativeEnum(AttributionSourceType).optional(),
  search: z.string().optional(),
  /** Filter admission groups by inventory id(s) */
  inventory: z.string().or(z.array(z.string())).optional(),
  deleted: z.coerce.boolean().optional(),
  lead: z.coerce.boolean().optional(),
})

// ---- admission group request ---- //
export const admissionGroupRequestCreateArgsSchema =
  admissionGroupCreateArgsSchema.omit({
    inventoryId: true,
    inventoryLevel: true,
    customerId: true,
    createdDiary: true,
    userId: true,
    managementUserId: true,
    status: true,
  })
